import { Grid, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import type { FC } from "react";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import PriceByDepartmentDialog from "../../orders/invoices/PriceByDepartmentDialog";
import ProductsTotalPrice from "../../orders/orderEdit/productsTable/ProductsTotalPrice";
import ProductPrice from "../../sharedComponents/products/ProductPrice";
import ProductName from "./ProductName";
import type { Product as ProductDetailsProduct } from "../productDetails/ProductDetails";

const ProductDate: FC<{ value: string }> = ({ value }) => (
  <Typography variant="subtitle2">{dayjs(value, ISO_DATE_FORMAT).format("DD.MM.YYYY")}</Typography>
);

const ProductTime: FC<{
  value: string | undefined;
}> = ({ value }) => <Typography variant="subtitle2">{value}</Typography>;

export interface ProductOption {
  readonly id: string;
  readonly name: string | undefined;
  readonly quantity: number;
  readonly price?: PriceValue | null;
}

interface OptionDetailsProps {
  readonly option: ProductOption | undefined;
  readonly currency: Currency;
  readonly exchangeRate: number;
  readonly hideProductPrices?: boolean;
}

const OptionDetails: FC<OptionDetailsProps> = ({ option, currency, exchangeRate, hideProductPrices }) => {
  if (!option) {
    return (
      <>
        <TableCell />
        {!hideProductPrices && <TableCell />}
        <TableCell />
      </>
    );
  }
  const price = Price.fromNullable(option.price).multiply(exchangeRate).round();
  return (
    <>
      <TableCell>
        <Typography>{option.name ?? "nd."}</Typography>
      </TableCell>
      {!hideProductPrices && <TableCell>{price.isPresent() ? `${price.value} ${currency}` : "n.d"}</TableCell>}
      <TableCell>{`${option.quantity} szt.`}</TableCell>
    </>
  );
};

export type Product = {
  readonly id: string;
  readonly name: string | undefined;
  readonly options: ProductOption[];
  readonly date: string;
  readonly time?: string;
  readonly exchangeRate: number | undefined;
  readonly price?: Price;
} & Pick<ProductDetailsProduct, "description" | "contact" | "images">;

interface ProductRowProps {
  readonly product: Product;
  readonly currency: Currency;
  readonly hideProductPrices?: boolean;
}

const ProductRows: FC<ProductRowProps> = ({ product, currency, hideProductPrices }) => {
  const [firstOption, ...otherOptions] = product.options;
  const price =
    product.price && product.exchangeRate !== undefined
      ? product.price.multiply(product.exchangeRate).round()
      : Price.onDemand;
  return (
    <>
      <TableRow data-testid="product-row">
        <TableCell rowSpan={product.options.length > 0 ? product.options.length : 1}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <ProductName productDetails={product}>
                <Typography variant="subtitle1">{product?.name ?? ""}</Typography>
              </ProductName>
            </Grid>
            <Grid size={7}>
              <ProductDate value={product.date} />
            </Grid>
            <Grid size={5}>
              <ProductTime value={product.time ?? ""} />
            </Grid>
          </Grid>
        </TableCell>
        <OptionDetails
          option={firstOption}
          currency={currency}
          exchangeRate={product.exchangeRate ?? 1}
          hideProductPrices={hideProductPrices}
        />
        {!hideProductPrices && (
          <TableCell rowSpan={product.options.length > 0 ? product.options.length : 1} align="right">
            <ProductPrice price={price} currency={currency} />
          </TableCell>
        )}
      </TableRow>
      {otherOptions.map((option, idx) => (
        <TableRow key={`${option.id}:${idx}`}>
          <OptionDetails
            option={option}
            currency={currency}
            exchangeRate={product.exchangeRate ?? 1}
            hideProductPrices={hideProductPrices}
          />
        </TableRow>
      ))}
    </>
  );
};

interface Props {
  readonly values: Product[];
  readonly currency: Currency;
  readonly hideProductPrices?: boolean;
  readonly totalPrice: Price;
  readonly orderId?: string;
}

const ProductsTable: FC<Props> = ({ values, currency, hideProductPrices, totalPrice, orderId }) => (
  <Table data-testid="e-commerce-offer-products">
    <TableHead>
      <TableRow>
        <TableCell>Produkt</TableCell>
        <TableCell>Nazwa opcji</TableCell>
        {!hideProductPrices && <TableCell>Cena jednostkowa</TableCell>}
        <TableCell>Ilość</TableCell>
        {!hideProductPrices && <TableCell align="right">Cena</TableCell>}
      </TableRow>
    </TableHead>
    <TableBody>
      {values.map((product, idx) => (
        <ProductRows
          key={`${idx}:${product.id}:${product.date}`}
          product={product}
          currency={currency}
          hideProductPrices={hideProductPrices}
        />
      ))}
    </TableBody>
    <TableFooter>
      <TableRow>
        <TableCell colSpan={hideProductPrices ? 2 : 4} />
        <TableCell align="right">
          <ProductsTotalPrice
            price={totalPrice}
            currency={currency}
            info={orderId ? <PriceByDepartmentDialog orderId={orderId} /> : undefined}
          />
        </TableCell>
      </TableRow>
    </TableFooter>
  </Table>
);

export default ProductsTable;
